import React, { useContext, useState } from "react";
import { Controller, useFieldArray, useWatch } from "react-hook-form";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
} from "@mui/material";
import PhotoUpload from "../../../components/PhotoUpload/PhotoUpload";
import { EditFormProps } from "../../../types/common";
import { MAIN_RULES } from "../../../constants/formRules";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import { EmployeeDataEdit } from "../../../types/api/employee";
import { MainContext, MainContextState } from "../../../context/MainContext";
import { getTags } from "../../../api/tagAPI";
import { TagData, TagType } from "../../../types/api/tag";
import { LIMIT } from "../../../constants/fiasAPI";
import { TAGS_TYPE } from "../../../constants/common";
import AsynchronousTags from "../../../components/Autocomplete/Asynchronous/AsynchronousTags";

interface IProps extends EditFormProps<EmployeeDataEdit> {
    tags: TagData[];
}

const EditContent = ({ form, tags }: IProps) => {
    const { constants } = useContext<Partial<MainContextState>>(MainContext);

    const [selectTagData, setSelectTagData] = useState<TagData | null>(null);
    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const { serviceTag, firstEducationTag, tagsObj } = tags.reduce(
        (acc, item) => {
            if (item.name.toLowerCase().includes("оказываемая услуга")) {
                acc.serviceTag = item;
                return acc;
            }
            if (!acc.firstEducationTag?.id && item.name.toLowerCase().includes("образование")) {
                acc.firstEducationTag = item;
                return acc;
            }

            acc.otherTags.push(item);
            acc.tagsObj[item.id] = item;

            return acc;
        },
        {
            serviceTag: {} as TagData,
            firstEducationTag: {} as TagData,
            otherTags: [] as TagData[],
            tagsObj: {} as Record<string, TagData>,
        },
    );

    const service = useWatch({
        control: form.control,
        name: "employee_type",
        defaultValue: undefined,
    });

    const workPositionEditArray = useFieldArray({
        control: form.control,
        name: "work_position",
    });

    const addWorkPositionEditInput = () => {
        workPositionEditArray.append("");
    };
    const deleteWorkPositionEditInput = (index: number) => {
        workPositionEditArray.remove(index);
    };

    const addFieldInput = () => {
        setOpen(true);
    };
    const deleteFieldInput = (tagId: string) => {
        const tempVals = form.getValues("work_info");
        delete tempVals[tagId];

        form.setValue("work_info", tempVals);
        form.unregister(`work_info.${tagId}`);
    };

    return (
        <>
            <Box
                component="form"
                sx={{
                    "& .MuiTextField-root": { my: 1 },
                }}
                noValidate
                autoComplete="off">
                <Controller
                    name="employee_type"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value }, fieldState }) => (
                        <FormControl sx={{ width: "100%" }}>
                            <InputLabel id={"employee-type-label"}>Тип работника</InputLabel>
                            <Select
                                size={"small"}
                                fullWidth
                                labelId={"employee-type-label"}
                                label={"Тип работника"}
                                onChange={(event: SelectChangeEvent) => {
                                    onChange(event.target.value);
                                }}
                                value={String(value)}
                                defaultValue={" "}
                                autoComplete="off"
                                error={!!fieldState.error}>
                                {constants?.employee_type.map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.text}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{fieldState.error?.message}</FormHelperText>
                        </FormControl>
                    )}
                />
                <Controller
                    defaultValue={""}
                    name="surname"
                    control={form.control}
                    rules={{
                        required: true,
                        maxLength: {
                            value: 120,
                            message: "Максимальная длина 120 символов",
                        },
                    }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            size={"small"}
                            fullWidth
                            label={"Фамилия"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={""}
                    name="name"
                    control={form.control}
                    rules={{
                        required: true,
                        maxLength: {
                            value: 120,
                            message: "Максимальная длина 120 символов",
                        },
                    }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            size={"small"}
                            fullWidth
                            label={"Имя"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={""}
                    name="lastname"
                    control={form.control}
                    rules={{
                        maxLength: {
                            value: 120,
                            message: "Максимальная длина 120 символов",
                        },
                    }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            size={"small"}
                            fullWidth
                            label={"Отчество"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={""}
                    name="phone"
                    control={form.control}
                    rules={{
                        required: true,
                        pattern: MAIN_RULES.phone,
                    }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Телефон"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={""}
                    name="email"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Почта"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={""}
                    name="from"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            type="date"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label="Дата начала"
                            onChange={(e: any) => {
                                onChange(e.target.value.replace(/-/g, "/"));
                            }}
                            value={value?.replace(/\//g, "-")}
                            autoComplete="off"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={""}
                    name="to"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            type="date"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label="Дата окончания сотрудничества"
                            onChange={(e: any) => {
                                onChange(e.target.value.replace(/-/g, "/"));
                            }}
                            value={value?.replace(/\//g, "-")}
                            autoComplete="off"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />

                {service === 2 && serviceTag && (
                    <Controller
                        defaultValue={""}
                        name={`work_info.${serviceTag?.id}`}
                        control={form.control}
                        rules={{
                            maxLength: {
                                value: 120,
                                message: "Максимальная длина 120 символов",
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                size={"small"}
                                fullWidth
                                label={"Оказываемая услуга"}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                )}

                {firstEducationTag && (
                    <Controller
                        defaultValue={""}
                        name={`work_info.${firstEducationTag?.id}`}
                        control={form.control}
                        rules={{
                            maxLength: {
                                value: 120,
                                message: "Максимальная длина 120 символов",
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                size={"small"}
                                fullWidth
                                label={firstEducationTag.name}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                )}
                {Object.keys(form.getValues("work_info") ?? {})?.map((tagId, index) => {
                    if (Number(tagId) === serviceTag.id || Number(tagId) === firstEducationTag.id) {
                        return null;
                    } else {
                        return (
                            <Controller
                                defaultValue={""}
                                key={index}
                                name={`work_info.${tagId}`}
                                control={form.control}
                                rules={{ required: true }}
                                render={({ field, fieldState }) => (
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <TextField
                                            {...field}
                                            size={"small"}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            label={
                                                {
                                                    ...tagsObj,
                                                    ...(selectTagData ? { [selectTagData.id]: selectTagData } : {}),
                                                }[tagId]?.name ?? "Доп. поле"
                                            }
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                        {index > 1 && (
                                            <IconButton
                                                aria-label="expand row"
                                                size="small"
                                                onClick={() => deleteFieldInput(tagId)}>
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                    </div>
                                )}
                            />
                        );
                    }
                })}
                <Box mb={1}>
                    <Tooltip title="Добавить доп. поля">
                        <IconButton aria-label="expand row" size="small" onClick={addFieldInput}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Controller
                    name="photos"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field: { value }, fieldState, formState: { errors } }) => {
                        return (
                            <PhotoUpload
                                formError={errors?.photos?.type === "required"}
                                error={fieldState.error}
                                field={{ photos: value }}
                                setValue={form.setValue}
                            />
                        );
                    }}
                />
                {workPositionEditArray?.fields.map((work, index) => (
                    <Controller
                        defaultValue={""}
                        key={work.id}
                        name={`work_position.${index}`}
                        control={form.control}
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <TextField
                                    {...field}
                                    size={"small"}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    fullWidth
                                    label={`Должность №${index + 1}`}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                                {index !== 0 && (
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        onClick={() => deleteWorkPositionEditInput(index)}>
                                        <ClearIcon />
                                    </IconButton>
                                )}
                            </div>
                        )}
                    />
                ))}
                <Tooltip title="Добавить должность">
                    <IconButton aria-label="expand row" size="small" onClick={addWorkPositionEditInput}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Добавление поля</DialogTitle>
                <DialogContent>
                    <DialogContentText pb={1}>Выберите название поля или введите новое значение</DialogContentText>
                    <AsynchronousTags
                        onChange={item => {
                            // setSelectTagData(item.name ?? "");
                        }}
                        value={selectTagData}
                        label="Название"
                        type={TagType.EMPLOYEE_WORK_INFO}
                        callbackResult={item => {
                            if (item.id) {
                                setSelectTagData(item as TagData);
                            }
                        }}
                        enableAdd
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Отмена</Button>
                    <Button
                        onClick={() => {
                            handleClose();
                            form.setValue(`work_info.${selectTagData?.id}`, "");
                        }}>
                        Подтвердить
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditContent;
